import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '@vnetwork/ui/primitives';

/**
 * Settings sub-sections that live on the bare `/buckets/:bucket` detail route.
 * The active one is carried in the `?tab=` search param (General is the default,
 * so it needs no param). Exported so the detail page renders the matching panel.
 */
export const BUCKET_SETTINGS_TABS = [
  { value: 'general', label: 'General' },
  { value: 'utilization', label: 'Utilization' },
  { value: 'tags', label: 'Tags' },
  { value: 'policy', label: 'Policy' },
  { value: 'logging', label: 'Logging' },
  { value: 'object-lock', label: 'Object Lock' },
] as const;

/**
 * Single bucket-level tab bar shared by the two sibling bucket pages so a user
 * inside a bucket can move between browsing objects and editing each settings
 * section from one row of tabs. Replaces the former stacked pair (an Objects |
 * Settings subnav on top of an inner settings Tabs) with a single, route-driven
 * bar: `Objects` is the `/objects` sibling route; every other tab is a settings
 * section on the `/buckets/:bucket` route selected via the `?tab=` param.
 */
export function BucketTabs({
  serviceId,
  bucket,
  settingsTab = 'general',
}: {
  serviceId: string;
  bucket: string;
  /** Active settings section on the detail route; ignored on the objects route. */
  settingsTab?: string;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const bucketBase = `/services/oss/${serviceId}/buckets/${bucket}`;
  const onObjects = pathname.startsWith(`${bucketBase}/objects`);
  const active = onObjects ? 'objects' : settingsTab;

  const go = (value: string) => {
    if (value === 'objects') {
      navigate(`${bucketBase}/objects`);
    } else {
      // General is the default section → keep the URL clean (no ?tab=general).
      navigate(value === 'general' ? bucketBase : `${bucketBase}?tab=${value}`);
    }
  };

  return (
    <Tabs value={active} onValueChange={go} style={{ marginBottom: 'var(--vn-space-4)' }}>
      <TabsList style={{ flexWrap: 'wrap' }}>
        <TabsTrigger value="objects">Objects</TabsTrigger>
        {BUCKET_SETTINGS_TABS.map((t) => (
          <TabsTrigger key={t.value} value={t.value}>
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

/** @deprecated Kept as a thin alias while call sites migrate to `BucketTabs`. */
export const BucketSubnav = BucketTabs;
